/**
 * scopeClassifier — lightweight, client-side heuristic that predicts whether
 * a keyword phrase is likely a *local-intent* search (e.g. "plumber near me",
 * "dentist bromsgrove") or a *national-intent* search (e.g. "seo dashboard").
 *
 * Used by the Edit Client dialog to suggest which scope textarea a pasted
 * keyword belongs in — never auto-migrates, always confirmed by the user.
 *
 * Tuning: "Balanced" preset — flags obvious local modifiers, UK/US postcodes,
 * a curated city seed list, and the "<service> in <place>" pattern. Everything
 * else is reported as "national" (which is the safe fallback for ambiguous
 * terms since the user paying attention will read the flagged list before
 * clicking "Move").
 */

// ── LOCAL SIGNAL A: Explicit local modifiers ──────────────────────────
// Case-insensitive, whole-phrase matches. Any hit → local.
const LOCAL_MODIFIER_RXS = [
  /\bnear\s?me\b/i,
  /\bnear\s?by\b/i,
  /\bin\s+my\s+area\b/i,
  /\bclose\s+to\s+me\b/i,
  /\baround\s+me\b/i,
  /\blocal\b/i,
  /\bnearest\b/i,
  /\bopen\s+now\b/i,
  /\bopen\s+late\b/i,
  /\b24\s?hour\b/i,
  /\bemergency\b/i,
];

// ── LOCAL SIGNAL B: Postcodes / ZIP codes ─────────────────────────────
// UK postcode:  SW1A 1AA, B60, WR9 8AB, etc.
const UK_POSTCODE_RX = /\b[a-z]{1,2}\d[a-z\d]?(?:\s?\d[a-z]{2})?\b/i;
// US ZIP:  90210 or 90210-1234
const US_ZIP_RX = /\b\d{5}(?:-\d{4})?\b/;

// ── LOCAL SIGNAL C: City / place seed list ────────────────────────────
// Curated list of well-known UK / US / CA / AU cities. Kept modest to avoid
// false positives on English words. Excluded common-word overlaps like
// "reading", "bath", "york", "hull" that a user might use as verbs/nouns
// in a non-local sense.
const PLACE_SEEDS = [
  // UK & Ireland
  "london","manchester","birmingham","liverpool","leeds","sheffield","bristol",
  "newcastle","nottingham","leicester","cardiff","edinburgh","glasgow","belfast",
  "dublin","aberdeen","southampton","portsmouth","brighton","oxford","cambridge",
  "coventry","bromsgrove","worcester","stourbridge","kidderminster","redditch",
  "droitwich","solihull","wolverhampton","stourport","dudley","walsall","tamworth",
  "stratford","cheltenham","gloucester","swindon","milton keynes","warrington",
  "wolverhampton","preston","blackpool","bolton","stoke","derby","peterborough",
  "norwich","ipswich","luton","watford","slough","croydon","sutton",
  "kingston","richmond","hammersmith","fulham","islington","hackney","camden",
  "wandsworth","southwark","greenwich","lewisham","enfield","harrow","brent",
  "ealing","hounslow","haringey","waltham","redbridge","newham","tower hamlets",
  "barnet","bexley","bromley","havering","hillingdon","kensington","chelsea",
  "westminster","lambeth","merton","romford","chelmsford","colchester","canterbury",
  "maidstone","tunbridge","hastings","eastbourne","bournemouth","poole","plymouth",
  "exeter","truro","penzance","salisbury","winchester","basingstoke","guildford",
  "woking","farnham","aldershot","crawley","horsham","tonbridge","dartford",
  // US (top 40 metros — keeping this trimmed to keep the seed list under control)
  "new york","los angeles","chicago","houston","phoenix","philadelphia",
  "san antonio","san diego","dallas","austin","san francisco","san jose",
  "seattle","denver","boston","atlanta","miami","portland","orlando","tampa",
  "nashville","charlotte","columbus","indianapolis","detroit","minneapolis",
  "kansas city","las vegas","milwaukee","albuquerque","tucson","fresno",
  "sacramento","memphis","louisville","baltimore","raleigh","cleveland",
  "pittsburgh","cincinnati","st louis","st paul","tampa bay",
  // Canada / AU / NZ
  "toronto","vancouver","montreal","calgary","ottawa","edmonton","winnipeg",
  "hamilton","halifax","sydney","melbourne","brisbane","perth","adelaide",
  "gold coast","auckland","wellington","christchurch",
];

// Build one big whole-word regex once at module load — cheap runtime match.
const PLACES_RX = new RegExp(
  `\\b(?:${PLACE_SEEDS
    .map((p) => p.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
    .join("|")})\\b`,
  "i",
);

// ── LOCAL SIGNAL D: <service> in <place> pattern ──────────────────────
// A common local-search shape is "<trade> in|near|around <somewhere>".
// If we see a known trade/service word AND a directional "in|near|around"
// preposition, it's almost certainly a local query.
const SERVICE_WORDS = new Set([
  "plumber","plumbing","electrician","electrical","dentist","doctor","gp",
  "lawyer","attorney","solicitor","accountant","chiropractor","therapist",
  "physio","physiotherapist","vet","veterinarian","mechanic","garage",
  "builder","roofer","roofing","cleaner","cleaning","gardener","gardening",
  "landscaper","landscaping","locksmith","glazier","joiner","carpenter",
  "tiler","tiling","painter","decorator","plasterer","tutor","tutoring",
  "photographer","photography","florist","baker","bakery","cafe","restaurant",
  "salon","barber","hairdresser","spa","gym","yoga","pilates","kennel",
  "kennels","boarding","groomer","grooming","trainer","training","daycare",
  "pizza","takeaway","catering","caterer","estate","realtor","carpets",
  "upholsterer","removals","mover","movers","towing","hvac","heating",
  "cooling","aircon","boiler","dogwalker","walker","childminder","nursery",
  "preschool","optician","optometrist","pharmacy","pharmacist","tailor",
  "seamstress","cobbler","dryclean","laundry","laundrette","printers",
  "printing","signwriter","upholstery","surveyor","architect","conveyancer",
  "notary","funeral","florist","framer","gunsmith","jeweller","jeweler",
  "cobbler","tattoo","tattooist","piercer","masseuse","massage","chef",
]);

const IN_NEAR_RX = /\b(?:in|near|around)\s+[a-z]/i;

/**
 * classifyScope — returns "local" | "national" | "unknown".
 *
 * "unknown" is reserved for blank input; callers that want a binary decision
 * can treat "unknown" the same as "national".
 */
export function classifyScope(keyword) {
  const kw = (keyword || "").trim().toLowerCase();
  if (!kw) return "unknown";

  // A. explicit local modifiers ("near me", "local plumber", "open now")
  for (const rx of LOCAL_MODIFIER_RXS) if (rx.test(kw)) return "local";

  // B. postcodes / ZIPs
  if (US_ZIP_RX.test(kw)) return "local";
  // UK postcode regex is broad — only fire when there is at least one digit +
  // letter combination (avoids matching bare service words). Additional gate:
  // the phrase must contain a digit at all.
  if (/\d/.test(kw) && UK_POSTCODE_RX.test(kw)) return "local";

  // C. known city / place seeds
  if (PLACES_RX.test(kw)) return "local";

  // D. "<service> in|near|around <place>" pattern
  const tokens = kw.split(/[\s\-/,]+/).filter(Boolean);
  const hasServiceWord = tokens.some((t) => SERVICE_WORDS.has(t));
  if (hasServiceWord && IN_NEAR_RX.test(kw)) return "local";

  return "national";
}

/**
 * bulkClassify — convenience helper that takes an array of keywords and
 * returns { local: [...], national: [...] } lists. Preserves order.
 */
export function bulkClassify(keywords) {
  const local = [];
  const national = [];
  for (const k of keywords || []) {
    const scope = classifyScope(k);
    if (scope === "local") local.push(k);
    else national.push(k);
  }
  return { local, national };
}
